import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { isEmbedded } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Check, ShoppingBag, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const PLAN_DETAILS = {
  starter: {
    name: "Starter", price: 29, orders: "250 Orders / Month", giveaways: "1 Active Giveaway",
    features: ["Auto entry tracking", "CSV exports"],
  },
  pro: {
    name: "Pro", price: 79, popular: true, orders: "2,000 Orders / Month", giveaways: "Unlimited Giveaways",
    features: ["Everything in Starter", "Winner picker", "AMOE portal", "Fraud detection", "Success Score", "Analytics"],
  },
  agency: {
    name: "Agency", price: 199, orders: "Unlimited Orders", giveaways: "Unlimited Giveaways",
    features: ["Everything in Pro", "White-label", "Multi-store", "Priority support", "API access"],
  },
};

export default function Billing() {
  const { user, checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const connected = !!user?.shopify_connected;

  useEffect(() => {
    const billing = params.get("billing");
    if (billing === "active") {
      toast.success("Subscription active — charged via your Shopify invoice.");
    } else if (billing === "declined") {
      toast.error("Subscription was declined or cancelled.");
    }
    if (billing) {
      setParams({});
      navigate("/billing", { replace: true });
    }
    // Reconcile plan with Shopify (covers interrupted redirects)
    (async () => {
      try {
        await api.get("/shopify/billing/status");
        await checkAuth();
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (plan_id) => {
    if (!connected) {
      toast.error("Connect your Shopify store first");
      return;
    }
    setLoading(plan_id || "manage");
    try {
      const { data } = await api.post("/shopify/billing/subscribe", { plan_id });
      // Shopify's pricing/approval page is an admin.shopify.com URL that cannot be
      // framed. When embedded, break out to the top level via App Bridge; otherwise
      // navigate normally.
      if (isEmbedded()) {
        window.open(data.confirmation_url, "_top");
      } else {
        window.location.href = data.confirmation_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open the pricing page");
      setLoading(null);
    }
  };

  const manageOnShopify = () => subscribe(null);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Plans & billing</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2 mb-2">Billing</h1>
        <p className="text-zinc-600 mb-2">
          Current plan: <span className="font-semibold text-zinc-950" data-testid="current-plan">{user?.plan === "free" ? "Free" : PLAN_DETAILS[user?.plan]?.name || user?.plan}</span>
          {user?.plan && user.plan !== "free" && (
            <button onClick={manageOnShopify} disabled={loading === "manage"} data-testid="manage-subscription-button" className="ml-3 text-xs font-medium text-zinc-600 hover:text-zinc-950 underline disabled:opacity-50">
              {loading === "manage" ? "Opening…" : "Manage / cancel on Shopify"}
            </button>
          )}
        </p>
        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-8">
          <ShoppingBag className="size-3.5" strokeWidth={1.5} /> Billed securely through Shopify — pick a plan on Shopify's pricing page; charges appear on your Shopify invoice.
        </div>

        {!connected && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex items-center justify-between gap-4" data-testid="billing-connect-notice">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <AlertCircle className="size-4" strokeWidth={1.5} /> Connect your Shopify store to subscribe — billing is handled through Shopify.
            </div>
            <Link to="/integrations" data-testid="billing-connect-shopify-link" className="text-xs font-medium text-amber-900 border border-amber-300 px-3 py-1.5 rounded-md hover:bg-amber-100 whitespace-nowrap">
              Connect Shopify →
            </Link>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 items-start">
          {Object.entries(PLAN_DETAILS).map(([id, p]) => {
            const current = user?.plan === id;
            return (
              <div
                key={id}
                className={`relative rounded-md border bg-white p-6 ${p.popular ? "border-zinc-950 ring-2 ring-zinc-950 md:-mt-2 md:pb-8 shadow-lg" : "border-zinc-200"}`}
                data-testid={`plan-card-${id}`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1 rounded-full" data-testid="popular-badge">
                    Most Popular
                  </div>
                )}
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="font-display text-4xl font-bold mt-3 tabular">
                  ${p.price}<span className="text-sm text-zinc-500 font-normal">/mo</span>
                </div>
                <div className={`mt-4 rounded-md px-3 py-2.5 ${p.popular ? "bg-emerald-50" : "bg-zinc-50"}`} data-testid={`plan-orders-${id}`}>
                  <div className={`font-display font-bold text-base ${p.popular ? "text-emerald-700" : "text-zinc-900"}`}>{p.orders}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.giveaways}</div>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-zinc-600">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="size-3.5 text-emerald-600 mt-0.5" strokeWidth={2} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(id)}
                  disabled={loading === id || current || !connected}
                  data-testid={`subscribe-${id}-button`}
                  className={`mt-6 w-full py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                    p.popular ? "bg-emerald-500 text-white hover:bg-emerald-600" : "border border-zinc-200 text-zinc-950 hover:bg-zinc-50"
                  }`}
                >
                  {current ? "Current plan" : loading === id ? "Redirecting to Shopify…" : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
